import {
  CARD_ICONS,
} from "../assets/icons";
import moment from "moment";
import { IoIosDocument } from "react-icons/io";
import { HiOutlineMapPin } from "react-icons/hi2";

const getCurrentTimeShipFormatted = () => {
  return moment().format("YYYY-MM-DD HH:mm");
};

const isUUID = (id) => {
  const regex = new RegExp(
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/
  );
  return regex.test(id);
};

export const locationToPaths = (pathname) => {
  let currentLink = "";
  return pathname
    .split("/")
    .filter((crumb) => crumb !== "")
    .map((crumb, i) => {
      currentLink += `/${crumb}`;
      return {
        id: i + 1,
        name: isUUID(crumb)
          ? crumb.charAt(0) + crumb.slice(1)
          : crumb.charAt(0).toUpperCase() + crumb.slice(1),
        link: currentLink,
      };
    });
};

export const createQuoteRequest = (data) => {
  return {
    from: data.isExistingAddress
      ? {
          zip: data.selectedAddress.address.zip,
          country: data.selectedAddress.address.country,
        }
      : {
          zip: data.from.zip,
          country: data.from.country,
        },
    to: {
      zip: data.to.zip,
      country: data.to.country,
    },
    item: {
      type: data.item.type.name,
      length: Number(data.item.length),
      width: Number(data.item.width),
      height: Number(data.item.height),
      weight: Number(data.item.weight),
    },
    measurement: data.item.isImperial ? "IMPERIAL" : "METRIC",
    shipDate: getCurrentTimeShipFormatted(),
  };
};

export const createQuoteRequestForShipRequest = (data, shipState) => {
  const quoteRequest = {
    from: shipState.from.isExistingAddress
      ? {
          name: shipState.from.existingAddress.address.name,
          address1: shipState.from.existingAddress.address.address1,
          ...(shipState.from.existingAddress.address.address2 !== "" && {
            address2: shipState.from.existingAddress.address.address2,
          }),
          city: shipState.from.existingAddress.address.city,
          state: {
            id: shipState.from.existingAddress.address.state.id,
            name: shipState.from.existingAddress.address.state.name,
          },
          country: {
            id: shipState.from.existingAddress.address.country.id,
            name: shipState.from.existingAddress.address.country.name,
          },
          zip: shipState.from.existingAddress.address.zip,
          phone: shipState.from.existingAddress.address.phone,
          email: shipState.from.existingAddress.address.email,
        }
      : {
          name: shipState.from.name,
          address1: shipState.from.address1,
          ...(shipState.from.address2 !== "" && {
            address2: shipState.from.address2,
          }),
          city: shipState.from.city,
          state: {
            id: shipState.from.state.id,
            name: shipState.from.state.name,
          },
          country: {
            id: shipState.from.country.id,
            name: shipState.from.country.name,
          },
          zip: shipState.from.zip,
          phone: shipState.from.phone,
          email: shipState.from.email,
        },
    to: {
      name: shipState.to.name,
      address1: shipState.to.address1,
      ...(shipState.to.address2 !== "" && { address2: shipState.to.address2 }),
      city: shipState.to.city,
      state: {
        id: shipState.to.state.id,
        name: shipState.to.state.name,
      },
      country: {
        id: shipState.to.country.id,
        name: shipState.to.country.name,
      },
      zip: shipState.to.zip,
      phone: shipState.to.phone,
      email: shipState.to.email,
      attention: shipState.to.name,
    },
    item: {
      type: data.type.name,
      length: Number(data.length),
      width: Number(data.width),
      height: Number(data.height),
      weight: Number(data.weight),
    },
    measurement: data.isImperial ? "IMPERIAL" : "METRIC",
    shipDate: getCurrentTimeShipFormatted(),
  };

  return quoteRequest;
};

export function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export const getExistingAddressOptions = (addresses) => {
  return (
    addresses?.map((a) => {
      if (!a) return null; // Handle potential undefined entries

      return {
        id: a.addressId,
        name: `${a.name}, ${a.address1}, ${a.address2 ? a.address2 + ", " : ""} ${
          a.city
        }, ${a.state?.name}, ${a.country?.name}, ${a.zip}, ${a.phone}, ${a.email}`,
        icon: (
          <div className="flex items-center justify-center w-7 h-7 rounded-md bg-slate-100 text-blue">
            <HiOutlineMapPin className="text-xl" />
          </div>
        ),
        address: a,
      };
    }) ?? [] // Provide a default empty array if addresses is undefined
  );
};

export const onAddressLoad = (data, setValue) => {
  let existingAddresses = getExistingAddressOptions(data.addresses);
  if (existingAddresses.length > 0) {
    setValue("isExistingAddress", true);
    setValue("selectedAddress", existingAddresses[0]);
  }
};
export const getExistingPaymentMethodOptions = (paymentMethods) => {
  return paymentMethods?.map((p) => {
    const nameLine = `•••• •••• •••• ${p.card.last4}`;

    const details = [];
    if (p.billing_details?.name) details.push(p.billing_details.name);
    if (p.billing_details?.address?.postal_code)
      details.push(p.billing_details.address.postal_code);
    if (p.billing_details?.address?.country)
      details.push(p.billing_details.address.country);

    const labelSub = details.length > 0 ? details.join(" • ") : undefined;

    return {
      id: p.id,
      name: nameLine,
      labelSub,
      icon: CARD_ICONS[p.card.brand],
      payment: p,
    };
  });
};
export const calculateServiceFee = (subtotal) => {
  let subtotalNumber = Number(subtotal).toFixed(2);
  return (subtotalNumber * 0.15).toFixed(2);
};

export const calculateTaxFee = (subtotal, serviceFee) => {
  let subtotalNumber = Number(subtotal);
  let serviceFeeNumber = Number(serviceFee);
  return ((subtotalNumber + serviceFeeNumber) * 0.13).toFixed(2);
};

export const calculateOrderTotal = (subtotal, serviceFee, taxFee) => {
  return Number(subtotal) + Number(serviceFee) + Number(taxFee);
};

export const createShippingRequest = (paymentMethodId, shipState) => {
  const isInternational = shipState.from.country.id !== shipState.to.country.id;

  return {
    paymentMethodId: paymentMethodId,
    serviceId: shipState.selectedRate.serviceId,
    from: shipState.from.isExistingAddress
      ? {
          name: shipState.from.existingAddress.address.name,
          address1: shipState.from.existingAddress.address.address1,
          ...(shipState.from.existingAddress.address.address2 !== "" && {
            address2: shipState.from.existingAddress.address.address2,
          }),
          city: shipState.from.existingAddress.address.city,
          state: {
            id: shipState.from.existingAddress.address.state.id,
            name: shipState.from.existingAddress.address.state.name,
          },
          country: {
            id: shipState.from.existingAddress.address.country.id,
            name: shipState.from.existingAddress.address.country.name,
          },
          zip: shipState.from.existingAddress.address.zip,
          phone: shipState.from.existingAddress.address.phone,
          email: shipState.from.existingAddress.address.email,
        }
      : {
          name: shipState.from.name,
          address1: shipState.from.address1,
          ...(shipState.from.address2 !== "" && {
            address2: shipState.from.address2,
          }),
          city: shipState.from.city,
          state: {
            id: shipState.from.state.id,
            name: shipState.from.state.name,
          },
          country: {
            id: shipState.from.country.id,
            name: shipState.from.country.name,
          },
          zip: shipState.from.zip,
          phone: shipState.from.phone,
          email: shipState.from.email,
        },
    to: {
      name: shipState.to.name,
      address1: shipState.to.address1,
      ...(shipState.to.address2 !== "" && { address2: shipState.to.address2 }),
      city: shipState.to.city,
      state: {
        id: shipState.to.state.id,
        name: shipState.to.state.name,
      },
      country: {
        id: shipState.to.country.id,
        name: shipState.to.country.name,
      },
      zip: shipState.to.zip,
      phone: shipState.to.phone,
      email: shipState.to.email,
      attention: shipState.to.name,
    },
    item: {
      type: shipState.item.type.name,
      length: Number(shipState.item.length),
      width: Number(shipState.item.width),
      height: Number(shipState.item.height),
      weight: Number(shipState.item.weight),
    },
    measurement: shipState.item.isImperial ? "IMPERIAL" : "METRIC",
    shipDate: getCurrentTimeShipFormatted(),
    insidePickup: false,
    saturdayPickup: false,
    insuranceType: "CARRIER",
    saveAddress: shipState.from.shouldSaveAddress,
    ...(isInternational && {
      invoice: shipState.item.invoice,
      invoiceCurrency: shipState.item.invoiceCurrency.id,
    }),
  };
};

export const getZipCodeInputLabel = (countryId) => {
  return countryId === "US" ? "Zip Code" : "Postal Code";
};

export const getStatesInputLabel = (countryId) => {
  return countryId === "US" ? "State" : "Province";
};

export const openLinkExternal = (link) => {
  return window.open(link);
};

export const transformOrdersSchema = (orders) => {
  if (!orders || orders.length === 0) return {};

  const sortedOrders = [...orders].sort(
    (a, b) => (b.orderDate ?? 0) - (a.orderDate ?? 0)
  );

  const groupedOrders = sortedOrders
    .map((o) => ({
      ...o,
      date: convertEpochToDayMonth(o.orderDate),
    }))
    .reduce((result, currentValue) => {
      const dateKey = `${currentValue.date.month} ${currentValue.date.day}, ${currentValue.date.year}`;

      if (!result[dateKey]) {
        result[dateKey] = [];
      }

      result[dateKey].push(currentValue);
      return result;
    }, {});

  return groupedOrders;
};

function convertEpochToDayMonth(epochMillis) {
  // Create a Date object from the epoch milliseconds
  const date = new Date(epochMillis);

  // Get day and month
  const day = date.getDate(); // Returns the day of the month (1-31)
  const month = date.getMonth(); // Returns the month (0-11)
  const year = date.getFullYear();

  // Month names array (optional, if you want month names instead of numbers)
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // Return day and month as an object
  return {
    day: day,
    month: monthNames[month], // Use monthNames array to get month name
    year: year,
  };
}

// Step 1: Define a function to group by a key
export function groupBy(array, key) {
  return array.reduce((result, currentValue) => {
    // Extract the value of the key to group by
    let groupKey = currentValue[key];

    // If the key doesn't exist in the result yet, create it
    if (!result[groupKey]) {
      result[groupKey] = [];
    }

    // Add the current object to the result array for its groupKey
    result[groupKey].push(currentValue);

    return result;
  }, {}); // Initialize accumulator as an empty object
}

export const addOrderAttachments = (order) => {
  const attachments = [];

  const toAttachment = (href, label) => {
    const url = new URL(href);
    // Decode the whole path, THEN split
    const filename = (
      decodeURIComponent(url.pathname).split("/").pop() || ""
    ).trim();
    return {
      filename: filename || "file.pdf",
      link: url,
      icon: <IoIosDocument />,
      label,
    };
  };

  if (order?.shippingLabelS3Url)
    attachments.push(toAttachment(order.shippingLabelS3Url, "Shipping Label"));

  if (order?.customsInvoiceS3Url)
    attachments.push(
      toAttachment(order.customsInvoiceS3Url, "Customs Invoice")
    );

  // Example for future payment invoice
  if (order?.paymentInvoiceS3Url)
    attachments.push(
      toAttachment(order.paymentInvoiceS3Url, "Payment Invoice")
    );

  return attachments;
};

export function getNestedValue(obj, path) {
  return path.split(".").reduce((acc, part) => acc && acc[part], obj);
}

export const mapSubdivisionsToSelectOptions = (subdivisions = []) => {
  if (!Array.isArray(subdivisions)) return [];

  return subdivisions.map((s) => ({
    id: s.subdivisionCode, // "CA-AB"
    name: s.name?.en,
  }));
};

export const mapCountriesToSelectOptions = (countries = []) => {
  return countries.map((c) => ({
    id: c.countryIso2, // "CA"
    name: c.name?.en || "", // "Canada"
  }));
};
